import React, { useState, useEffect } from "react";
import { Text, StyleSheet, View, TouchableOpacity } from "react-native";

import moment from "moment";
import { find } from "lodash";
import cardStyles from "../cards/cardStyles";

import LoadingIndicator from "../../common/LoadingIndicator";
import Icons from "../../common/icons/index";
import TimeSlotItemStartTime from "../../journal/timeslots/time/TimeSlotItemStartTime";
import TimeSlotItemEndTime from "../../journal/timeslots/time/TimeSlotItemEndTime";
import SleepActivity from "./SleepActivity";
import JournalHelper from "../../journal/JournalHelper";
import Utils, { navigate } from "../../../../helpers/utils";
import appStyles from "../../../appStyles";
import appPages from "../../../../appPages";

const DATE_FORMAT = "YYYY-MM-DD";
const FULL_DATE_FORMAT = "YYYY-MM-DD HH:mm:ss";

export default function SleepCard({
  controller,
  timeslot,
  onConfirmComplete = () => {},
  toggleTimePickerDialog = () => {},
}) {
  const [showSleepCard, setShowSleepCard] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(0);
  const [timeslotModel, setTimeslotModel] = useState({});

  useEffect(() => {
    if (!timeslot || !timeslot.id) return;
    setIsLoading(false);

    fetchTimeslots();
  }, [timeslot]);

  const fetchTimeslots = () => {
    const sleepCardData = find(
      controller.state.cards_v2,
      (c) => c.type === "sleep",
    );

    if (!sleepCardData || !sleepCardData.visible) return;

    const { activity, visible, predicted_end_time } = sleepCardData;

    const times = visible.split("-");
    if (!times || times.length < 2) return;

    const timeslotFullDate = moment(timeslot.time);
    const timeslotDate = timeslotFullDate.format(DATE_FORMAT);
    const card_startTime = moment(
      `${timeslotDate} ${times[0]}`,
      FULL_DATE_FORMAT,
    );
    const card_endTime = moment(
      `${timeslotDate} ${times[1]}`,
      FULL_DATE_FORMAT,
    );
    if (card_endTime.isBefore(card_startTime)) {
      card_endTime.add(1, "days");
    }

    const timeslotHMS = timeslotFullDate.format("HH:mm:ss");
    const startHMS = card_startTime.format("HH:mm:ss");
    const endHMS = card_endTime.format("HH:mm:ss");

    setShowSleepCard(
      (startHMS < endHMS && startHMS <= timeslotHMS && timeslotHMS < endHMS) ||
        (startHMS > endHMS &&
          (timeslotHMS >= startHMS || timeslotHMS < endHMS)),
    );

    let predictedEndDate = predicted_end_time
      ? moment(`${timeslotDate} ${predicted_end_time}`, FULL_DATE_FORMAT)
      : Utils.nowUserTz(controller);

    if (predictedEndDate < timeslotFullDate) {
      predictedEndDate = predictedEndDate.add(1, "days");
    }
    setTimeslotModel({
      ...timeslot,
      endDate: predictedEndDate.format(FULL_DATE_FORMAT),
      endTime: predictedEndDate.format("H:mm A"),
      activity: activity,
      source: "sleep",
    });

    setSelectedDate(
      [
        [timeslotFullDate.format("ddd"), predictedEndDate.format("ddd")].join(
          " - ",
        ),
        [timeslotFullDate.format("M/D"), predictedEndDate.format("M/D")].join(
          " - ",
        ),
      ].join(", "),
    );
    setIsLoading(false);
  };

  const goToEditPage = () => {
    navigate(
      controller,
      appPages.trackDetail,
      "changePage",
      {
        timeslot: timeslotModel,
        sourceUrl: "rn:sleep_card",
        returnUrl: "rn:home_card",
      },
    );
  };

  const confirmTimeslot = () => {
    controller.tracking.track("Sleep Card Confirm");
    JournalHelper.saveTimeslotToDB(
      controller,
      controller.state.timeslots || [],
      {
        timeslot: timeslotModel,
        reload: true,
        was_predicted: 1,
        was_first_option: 1,
        sourceOverride: "sleep",
      },
      onConfirmComplete,
    );
  };

  return !showSleepCard ? null : (
    <View style={cardStyles.container}>
      <View style={[cardStyles.card, { minHeight: 120 }]}>
        <LoadingIndicator isLoading={isLoading} />

        <View
          style={[
            cardStyles.header,
            { paddingHorizontal: 0, paddingBottom: 16 },
          ]}
        >
          <Icons.HourGlassIcon />

          {!isLoading ? (
            <TouchableOpacity
              style={[cardStyles.headerInfo, { marginLeft: 6 }]}
            >
              <Text style={cardStyles.title}>{selectedDate}</Text>
            </TouchableOpacity>
          ) : null}
        </View>

        <View style={[appStyles.timeslotRow, { marginLeft: 6 }]}>
          <TimeSlotItemStartTime
            timeslot={timeslotModel}
            index={0}
            style={styles.startTime}
            onPress={() => toggleTimePickerDialog(timeslotModel)}
          />
          <TimeSlotItemEndTime
            timeslot={timeslotModel}
            isHomeCard={true}
            onPress={() => toggleTimePickerDialog(timeslotModel)}
          />

          <Icons.More
            onPress={goToEditPage}
            style={[appStyles.iconContainer, styles.editIcon]}
          />

          {timeslotModel.activity ? (
            <SleepActivity
              timeslot={timeslotModel}
              editMode={true}
              onActivitySelected={confirmTimeslot}
            />
          ) : null}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  editIcon: {
    position: "absolute",
    top: 5,
    left: 0,
  },
});
